import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  MessageCircle,
  Send,
  Hash,
  Plus,
  Users,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora mismo";
  if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ROLE_LABEL = {
  admin: "Admin",
  gerente: "Gerente",
  recepcionista: "Recepción",
  audioprotesista: "Audioprotesista",
};

export default function Chat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmCandidates, setDmCandidates] = useState([]);
  const scrollRef = useRef(null);
  const sinceRef = useRef(null);
  const pollRef = useRef(null);

  const loadChannels = async () => {
    const { data } = await api.get("/chat/channels");
    setChannels(data || []);
    if (data && data.length > 0 && !activeChannel) {
      setActiveChannel(data[0].channel);
    }
  };

  useEffect(() => {
    loadChannels();
    // eslint-disable-next-line
  }, []);

  const loadAll = async (ch) => {
    if (!ch) return;
    try {
      const { data } = await api.get("/chat/messages", { params: { channel: ch } });
      setMessages(data);
      sinceRef.current = data.length ? data[data.length - 1].created_at : null;
    } catch (err) {
      // silent
    }
  };

  const poll = async () => {
    if (!activeChannel || !sinceRef.current) return;
    try {
      const { data } = await api.get("/chat/messages", {
        params: { channel: activeChannel, since: sinceRef.current },
      });
      if (data.length) {
        setMessages((prev) => [...prev, ...data]);
        sinceRef.current = data[data.length - 1].created_at;
      }
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    if (!activeChannel) return;
    setMessages([]);
    sinceRef.current = null;
    loadAll(activeChannel);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [activeChannel]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content || sending || !activeChannel) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { content, channel: activeChannel });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const openDmDialog = async () => {
    try {
      const { data } = await api.get("/chat/users");
      setDmCandidates(data);
      setDmOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startDm = async (uid) => {
    try {
      const { data } = await api.post(`/chat/dm/${uid}`);
      setDmOpen(false);
      await loadChannels();
      setActiveChannel(data.channel);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const centerChannels = channels.filter((c) => c.kind === "center");
  const dmChannels = channels.filter((c) => c.kind === "dm");
  const active = channels.find((c) => c.channel === activeChannel);
  const activeLabel = active?.label || "—";
  const activeIsDM = active?.kind === "dm";

  return (
    <div className="space-y-6" data-testid="chat-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Equipo
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Chat interno
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Canales públicos por centro y mensajes directos entre compañeros.
          </p>
        </div>

        <Dialog open={dmOpen} onOpenChange={setDmOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              onClick={openDmDialog}
              data-testid="new-dm-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-1" /> Nuevo chat privado
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Iniciar chat privado</DialogTitle>
            </DialogHeader>
            <div className="space-y-1 max-h-[50vh] overflow-y-auto">
              {dmCandidates.length === 0 && (
                <div className="text-sm text-[#6E7772] py-6 text-center">
                  No hay otros usuarios disponibles para iniciar un chat.
                </div>
              )}
              {dmCandidates.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => startDm(u.id)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#F7F6F3] transition-colors"
                  data-testid={`dm-pick-${u.id}`}
                >
                  <div className="h-9 w-9 rounded-full bg-[#EAE6DF] text-[#4A5D4E] flex items-center justify-center font-medium text-sm">
                    {(u.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-[#2A2F2C] truncate">{u.name}</div>
                    <div className="text-xs text-[#6E7772]">{ROLE_LABEL[u.role] || u.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {channels.length === 0 ? (
        <Card className="bg-white border-[#E5E3DB] shadow-none">
          <CardContent className="p-12 text-center text-[#6E7772]">
            No tienes un centro asignado. Pide al administrador que te asigne uno.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
          {/* Sidebar canales */}
          <Card className="bg-white border-[#E5E3DB] shadow-none">
            <CardContent className="p-3 space-y-3">
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#6E7772] px-2 mb-1.5 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Canales del equipo
                </div>
                <div className="space-y-0.5">
                  {centerChannels.length === 0 && (
                    <div className="text-xs text-[#6E7772] italic px-2 py-1">Ninguno</div>
                  )}
                  {centerChannels.map((c) => (
                    <button
                      key={c.channel}
                      type="button"
                      onClick={() => setActiveChannel(c.channel)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        activeChannel === c.channel
                          ? "bg-[#EAE6DF] text-[#2A2F2C] font-medium"
                          : "text-[#4B5563] hover:bg-[#F7F6F3]"
                      }`}
                      data-testid={`chan-${c.channel}`}
                    >
                      <Hash className="h-3.5 w-3.5" />
                      <span className="truncate">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#6E7772] px-2 mb-1.5 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Mensajes directos
                </div>
                <div className="space-y-0.5">
                  {dmChannels.length === 0 && (
                    <div className="text-xs text-[#6E7772] italic px-2 py-1">
                      Sin chats privados todavía
                    </div>
                  )}
                  {dmChannels.map((c) => (
                    <button
                      key={c.channel}
                      type="button"
                      onClick={() => setActiveChannel(c.channel)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        activeChannel === c.channel
                          ? "bg-[#EAE6DF] text-[#2A2F2C] font-medium"
                          : "text-[#4B5563] hover:bg-[#F7F6F3]"
                      }`}
                      data-testid={`chan-${c.channel}`}
                    >
                      <div className="h-6 w-6 rounded-full bg-[#EAE6DF] text-[#4A5D4E] flex items-center justify-center text-[11px] font-medium">
                        {(c.label || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <span className="truncate">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hilo */}
          <Card className="bg-white border-[#E5E3DB] shadow-none">
            <CardContent className="p-0 flex flex-col h-[68vh]">
              <div className="px-5 py-3 border-b border-[#E5E3DB] flex items-center gap-2">
                {activeIsDM ? (
                  <Lock className="h-4 w-4 text-[#6E7772]" />
                ) : (
                  <Hash className="h-4 w-4 text-[#6E7772]" />
                )}
                <div className="font-medium text-[#2A2F2C]" data-testid="chat-channel-name">
                  {activeLabel}
                </div>
                {activeIsDM && (
                  <span className="text-[11px] text-[#6E7772] ml-1">(privado · solo tú y {activeLabel})</span>
                )}
              </div>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-5 space-y-3 scrollbar-thin"
                data-testid="chat-thread"
              >
                {messages.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-[#6E7772]">
                    <MessageCircle className="h-10 w-10 mb-3 opacity-50" strokeWidth={1.5} />
                    <div className="text-sm">
                      Sin mensajes en <strong>{activeLabel}</strong> todavía.
                    </div>
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      data-testid={`msg-${m.id}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3.5 py-2 text-sm ${
                          mine
                            ? "bg-[#4A5D4E] text-white"
                            : "bg-[#EAE6DF] text-[#2A2F2C]"
                        }`}
                      >
                        {!mine && (
                          <div className="text-[11px] font-medium text-[#4A5D4E] mb-0.5">
                            {m.sender_name}
                          </div>
                        )}
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                        <div
                          className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#6E7772]"}`}
                        >
                          {relTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form
                onSubmit={send}
                className="border-t border-[#E5E3DB] p-3 flex gap-2"
                data-testid="chat-form"
              >
                <Input
                  data-testid="chat-input"
                  placeholder={`Escribe un mensaje en ${activeLabel}…`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  disabled={sending || !text.trim()}
                  data-testid="chat-send"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
